import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { apiGet, apiPatch, apiPut, apiPost, toAbsUrl, createShippingLabel, getLabelPreflight } from "../utils/api";
import { getDisplayImage } from "../utils/productImage";
import "../styles/shopOrders.css";

// Paid by any rail (card, MSC, mixed; payout transfer states are paid too).
const PAID_STATUSES = ["paid", "paid_msc", "paid_mixed", "transferred", "transfer_pending", "transfer_blocked_currency", "processing", "shipped", "delivered", "completed", "partially_refunded"];
// Paid and still waiting to be shipped.
const PAID_UNSHIPPED = ["paid", "paid_msc", "paid_mixed", "transferred", "transfer_pending", "transfer_blocked_currency", "processing"];
const PAGE_SIZE = 100;

function orderIsPaid(order) {
  const st = String(order?.status || "").toLowerCase();
  return order?.isPaid === true || order?.payment?.paid === true || PAID_STATUSES.includes(st);
}

export default function ShopOrders() {
  const [orders, setOrders] = useState([]);
  const [returns, setReturns] = useState({}); // orderId -> return object
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [selected, setSelected] = useState(new Set());
  const [bulkLoading, setBulkLoading] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);

  const buildQs = (extra = {}) => {
    const p = new URLSearchParams();
    if (statusFilter) p.set("status", statusFilter);
    p.set("limit", String(PAGE_SIZE));
    Object.entries(extra).forEach(([k, v]) => { if (v) p.set(k, v); });
    return `?${p.toString()}`;
  };

  const loadOrders = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [ordersData, returnsData] = await Promise.all([
        apiGet(`/shop/orders${buildQs()}`),
        apiGet("/shop/returns").catch(() => ({ returns: [] })),
      ]);
      const list = Array.isArray(ordersData) ? ordersData : (ordersData?.orders || ordersData?.items || []);
      setOrders(list);
      setHasMore(ordersData?.hasMore === true || list.length >= PAGE_SIZE);

      // Build returns map keyed by orderId. The list is newest first, so keep
      // the first (latest) return per order.
      const retList = Array.isArray(returnsData) ? returnsData : (returnsData?.returns || returnsData?.items || []);
      const retMap = {};
      for (const r of retList) {
        const oid = String(r?.orderId?._id || r?.orderId || "");
        if (oid && !retMap[oid]) retMap[oid] = r;
      }
      setReturns(retMap);

      if (isInitialLoad) {
        window.scrollTo({ top: 0, behavior: "smooth" });
        setIsInitialLoad(false);
      }
    } catch (err) {
      setError(err.message || "Failed to load orders");
    } finally {
      setLoading(false);
    }
  }, [statusFilter, isInitialLoad]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { loadOrders(); }, [statusFilter]); // eslint-disable-line

  // "Load more": the next page of older orders (?before=<oldest createdAt>).
  const loadMore = useCallback(async () => {
    if (!orders.length) return;
    const oldest = orders.reduce((min, o) => (!min || new Date(o.createdAt) < new Date(min) ? o.createdAt : min), null);
    if (!oldest) return;
    setLoadingMore(true);
    try {
      const data = await apiGet(`/shop/orders${buildQs({ before: new Date(oldest).toISOString() })}`);
      const list = Array.isArray(data) ? data : (data?.orders || data?.items || []);
      setOrders((prev) => {
        const seen = new Set(prev.map((o) => String(o._id)));
        return [...prev, ...list.filter((o) => !seen.has(String(o._id)))];
      });
      setHasMore(data?.hasMore === true || list.length >= PAGE_SIZE);
    } catch (err) {
      alert(err?.message || "Failed to load more orders");
    } finally {
      setLoadingMore(false);
    }
  }, [orders, statusFilter]); // eslint-disable-line

  const stats = useMemo(() => {
    const s = { total: orders.length, paid: 0, shipped: 0, completed: 0, cancelled: 0 };
    // Map every real status into one of the visible buckets (paid_msc, transferred,
    // delivered, etc. must not fall through — that's why PAID used to show 0).
    const bucket = (status) => {
      const st = String(status || "pending").toLowerCase();
      if (["completed", "delivered"].includes(st)) return "completed";
      if (["shipped", "in_transit", "out_for_delivery"].includes(st)) return "shipped";
      if (["paid", "paid_msc", "paid_mixed", "transferred", "transfer_pending", "transfer_blocked_currency"].includes(st)) return "paid";
      if (["cancelled", "refunded"].includes(st)) return "cancelled";
      return null;
    };
    orders.forEach((o) => {
      const b = bucket(o.status);
      if (b && s[b] !== undefined) s[b]++;
    });
    return s;
  }, [orders]);

  const sorted = useMemo(() => {
    return [...orders].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
  }, [orders]);

  const handleStatusChange = async (orderId, newStatus) => {
    try {
      await apiPatch(`/shop/orders/${orderId}/status`, { status: newStatus });
      setOrders((prev) => prev.map((o) => (o._id === orderId ? { ...o, status: newStatus } : o)));
    } catch (err) {
      alert(err.message);
    }
  };

  const handleOrderUpdate = useCallback((updatedOrder) => {
    setOrders((prev) => prev.map((o) => (o._id === updatedOrder._id ? { ...o, ...updatedOrder } : o)));
  }, []);

  const handleReturnUpdate = useCallback((orderId, updatedReturn) => {
    setReturns((prev) => ({ ...prev, [orderId]: updatedReturn }));
  }, []);

  // Bulk selection
  const toggleSelect = useCallback((id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }, []);

  const selectAll = useCallback(() => {
    if (selected.size === sorted.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(sorted.map((o) => o._id)));
    }
  }, [sorted, selected.size]);

  const handleBulkStatus = useCallback(async (newStatus) => {
    let ids = Array.from(selected);
    if (ids.length === 0) return;
    const byId = new Map(orders.map((o) => [o._id, o]));
    let skipped = 0;
    if (newStatus === "cancelled") {
      // A paid order is cancelled through Refund (so the buyer gets the money back).
      const unpaid = ids.filter((id) => !orderIsPaid(byId.get(id)));
      skipped = ids.length - unpaid.length;
      ids = unpaid;
      if (!ids.length) {
        alert("All selected orders are paid. Use Refund on each order to cancel it and return the buyer's money.");
        return;
      }
    }
    if (newStatus === "shipped") {
      const ready = ids.filter((id) => PAID_UNSHIPPED.includes(String(byId.get(id)?.status || "").toLowerCase()));
      skipped = ids.length - ready.length;
      ids = ready;
      if (!ids.length) {
        alert("None of the selected orders are paid and waiting to ship.");
        return;
      }
    }
    if (!window.confirm(`Update ${ids.length} order(s) to "${newStatus}"?${skipped ? ` (${skipped} skipped)` : ""}`)) return;
    setBulkLoading(true);
    let successCount = 0;
    const failures = [];
    for (const id of ids) {
      try {
        await apiPatch(`/shop/orders/${id}/status`, { status: newStatus });
        setOrders((prev) => prev.map((o) => (o._id === id ? { ...o, status: newStatus } : o)));
        successCount++;
      } catch (err) {
        console.warn(`Bulk update failed for ${id}:`, err.message);
        failures.push(`#${String(id).slice(-6).toUpperCase()}: ${err?.message || "failed"}`);
      }
    }
    setBulkLoading(false);
    setSelected(new Set());
    let msg = `Updated ${successCount}/${ids.length} orders to ${newStatus}.`;
    if (skipped) msg += ` ${skipped} skipped.`;
    if (failures.length) msg += `\n\nFailed:\n${failures.slice(0, 10).join("\n")}${failures.length > 10 ? `\n…and ${failures.length - 10} more` : ""}`;
    alert(msg);
  }, [selected, orders]);

  const handleBulkCreateLabels = useCallback(async () => {
    const ids = Array.from(selected);
    if (ids.length === 0) return;
    if (!window.confirm(`Create shipping labels for ${ids.length} order(s)?`)) return;
    setBulkLoading(true);
    let successCount = 0;
    const failures = [];
    for (const id of ids) {
      try {
        await createShippingLabel(id);
        successCount++;
      } catch (err) {
        console.warn(`Label creation failed for ${id}:`, err.message);
        failures.push(`#${String(id).slice(-6).toUpperCase()}: ${err?.data?.message || err?.message || "failed"}`);
      }
    }
    setBulkLoading(false);
    setSelected(new Set());
    let msg = `Created ${successCount}/${ids.length} labels.`;
    if (failures.length) msg += `\n\nFailed:\n${failures.slice(0, 10).join("\n")}${failures.length > 10 ? `\n…and ${failures.length - 10} more` : ""}`;
    alert(msg);
    if (successCount > 0) loadOrders();
  }, [selected, loadOrders]);

  // No "pending" tab: unpaid orders are never shown to the shop.
  const statuses = ["", "paid", "shipped", "completed", "cancelled", "refunded"];

  return (
    <div className="so-page page-responsive">
      {/* Header */}
      <div className="so-header">
        <div>
          <h1 className="so-title">Orders</h1>
          <p className="so-subtitle">Manage orders, fulfillment, returns & refunds</p>
        </div>
        <button className="so-btn-refresh" onClick={loadOrders} disabled={loading}>
          {loading ? "⟳ Refreshing..." : "⟳ Refresh"}
        </button>
      </div>

      {/* Stats bar */}
      <div className="so-stats">
        <div className="so-stat"><span className="so-stat-value">{stats.total}</span><span className="so-stat-label">Total</span></div>
        <div className="so-stat so-stat-paid"><span className="so-stat-value">{stats.paid}</span><span className="so-stat-label">Paid</span></div>
        <div className="so-stat so-stat-shipped"><span className="so-stat-value">{stats.shipped}</span><span className="so-stat-label">Shipped</span></div>
        <div className="so-stat so-stat-completed"><span className="so-stat-value">{stats.completed}</span><span className="so-stat-label">Delivered</span></div>
      </div>

      {/* Filters */}
      <div className="so-filters">
        {statuses.map((s) => (
          <button key={s} className={`so-pill ${statusFilter === s ? "so-pill-active" : ""}`} onClick={() => setStatusFilter(s)}>
            {s === "" ? "All" : s.charAt(0).toUpperCase() + s.slice(1)}
          </button>
        ))}
      </div>

      {/* Bulk Action Bar */}
      {selected.size > 0 && (
        <div className="so-bulk-bar">
          <div className="so-bulk-info">
            <input type="checkbox" checked={selected.size === sorted.length} onChange={selectAll} />
            <span>{selected.size} selected</span>
          </div>
          <div className="so-bulk-actions" data-no-expand="true">
            {bulkLoading ? <span className="so-updating">Processing...</span> : (
              <>
                <button className="so-btn so-btn-primary so-btn-sm" onClick={() => handleBulkStatus("shipped")}>🚚 Ship</button>
                <button className="so-btn so-btn-primary so-btn-sm" onClick={handleBulkCreateLabels}>📦 Create Labels</button>
                <button className="so-btn so-btn-danger so-btn-sm" onClick={() => handleBulkStatus("cancelled")}>Cancel</button>
                <button className="so-btn so-btn-default so-btn-sm" onClick={() => setSelected(new Set())}>✕ Clear</button>
              </>
            )}
          </div>
        </div>
      )}

      {/* Content */}
      {error ? (
        <div className="so-error">{error}</div>
      ) : sorted.length === 0 && !loading ? (
        <div className="so-empty">
          <div className="so-empty-icon">📦</div>
          <div className="so-empty-text">No orders yet</div>
          <div className="so-empty-sub">Orders from customers will appear here</div>
        </div>
      ) : (
        <div className="so-list">
          {sorted.map((order) => (
            <OrderCard
              key={order._id}
              order={order}
              returnReq={returns[String(order._id)] || null}
              onStatus={handleStatusChange}
              onUpdate={handleOrderUpdate}
              onReturnUpdate={handleReturnUpdate}
              isSelected={selected.has(order._id)}
              onToggleSelect={toggleSelect}
            />
          ))}
          {hasMore && (
            <div style={{ textAlign: "center", margin: "16px 0" }}>
              <button className="so-btn so-btn-default" onClick={loadMore} disabled={loadingMore}>
                {loadingMore ? "Loading..." : "Load more"}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* =========================================================
   ORDER CARD
   ========================================================= */
function OrderCard({ order, returnReq, onStatus, onUpdate, onReturnUpdate, isSelected, onToggleSelect }) {
  const [expanded, setExpanded] = useState(false);
  const [labelLoading, setLabelLoading] = useState(false);
  const [labelError, setLabelError] = useState("");
  const [statusUpdating, setStatusUpdating] = useState(false);
  const [returnUpdating, setReturnUpdating] = useState(false);
  const [refundModalOpen, setRefundModalOpen] = useState(false);
  const [refundAmount, setRefundAmount] = useState("");
  const [refundNote, setRefundNote] = useState("");
  const [refundLoading, setRefundLoading] = useState(false);
  const [refundError, setRefundError] = useState("");
  const [trackCarrier, setTrackCarrier] = useState("usps");
  const [trackNumber, setTrackNumber] = useState("");
  const [trackSaving, setTrackSaving] = useState(false);
  const [trackError, setTrackError] = useState("");
  const nav = useNavigate();

  const product = order.product || {};
  const productId = product._id || product.id || order.productId || "";
  const img = toAbsUrl(getDisplayImage(product) || order.productImage || "");

  // Render every line item. Fall back to the legacy single-product fields for
  // old orders that predate the items[] array.
  const lineItems =
    Array.isArray(order.items) && order.items.length
      ? order.items
      : [
          {
            productId,
            title: product.title || order.productTitle,
            image: img,
            qty: Number(order.quantity || 1),
          },
        ];
  const customerName = order.buyerName || order.user?.displayName || order.user?.email || "Guest";
  const customerEmail = order.buyerEmail || order.user?.email || "N/A";
  const currency = (order.currency || "USD").toUpperCase();
  const total = Number(order.total || order.amount || 0);
  // What the shop actually earns = items subtotal − 6% Moondala fee.
  // (Shipping + card processing are buyer-funded and not the shop's concern.)
  const itemsSubtotal =
    Number(order?.amounts?.subtotalCents ? order.amounts.subtotalCents / 100 : 0) ||
    Number(order?.subtotal || 0) ||
    total;
  const shopFee = Math.round(itemsSubtotal * 0.06 * 100) / 100;
  const shopEarn = Math.round((itemsSubtotal - shopFee) * 100) / 100;
  const quantity = Number(order.quantity || 1);
  const orderId = order._id || "";
  const shortId = orderId.slice(-6).toUpperCase();
  const date = order.createdAt ? new Date(order.createdAt).toLocaleDateString() : "";
  const fullDate = order.createdAt ? new Date(order.createdAt).toLocaleString() : "";

  // Payment
  const paymentMethod = order.payment?.method || order.paymentProvider || "N/A";
  const isPaid = orderIsPaid(order);

  // Fulfillment
  const status = (order.status || "pending").toLowerCase();
  const shippingStatus = order.shipping?.status || "pending";
  const hasLabel = !!(order.shipping?.shipstation?.labelId || order.shipping?.label?.labelId);
  const trackingNum = order.shipping?.shipstation?.trackingNumber || order.shipping?.tracking?.trackingNumber || "";
  const trackingUrl = order.shipping?.tracking?.trackingUrl || "";
  const labelUrl = order.shipping?.shipstation?.labelUrl || order.shipping?.label?.labelUrl || "";

  // Shipping address
  const addr = order.shippingAddressSnapshot || order.shipping?.addressTo || order.shipping?.toAddress || null;

  // Options
  const options = order.selectedOptions || order.lineItem?.selectedOptions || [];

  // Shipping rate
  const selectedRate = order.shipping?.buyerSelectedRate;

  // Return info
  const hasReturn = !!returnReq;
  const returnStatus = hasReturn ? (returnReq.status || "requested").toLowerCase() : "";
  const returnReason = returnReq?.reason || "";
  const returnNote = returnReq?.note || "";
  const returnShopNote = returnReq?.shopNote || "";
  const returnId = returnReq?._id || "";
  const returnRefundAmount = Number(returnReq?.refundAmount || total);

  // Fulfillment status
  const fulfillmentStatus = (() => {
    if (status === "cancelled" || status === "refunded") return status;
    if (shippingStatus === "delivered" || status === "completed") return "delivered";
    if (shippingStatus === "in_transit" || status === "shipped") return "shipped";
    if (shippingStatus === "out_for_delivery") return "out_for_delivery";
    if (hasLabel || shippingStatus === "label_created") return "label_created";
    return "unfulfilled";
  })();

  const toggleExpand = (e) => {
    if (!e.target.closest("[data-no-expand]")) setExpanded(!expanded);
  };

  // Create label
  const handleCreateLabel = async () => {
    setLabelLoading(true);
    setLabelError("");
    try {
      try {
        await getLabelPreflight(orderId);
      } catch (preErr) {
        const code = preErr?.data?.code || "";
        if (code === "LABEL_EXISTS") { setLabelError("Label already exists"); setLabelLoading(false); return; }
        if (code === "ORDER_NOT_PAID") { setLabelError("Order must be paid first"); setLabelLoading(false); return; }
        if (code === "RATE_NOT_SELECTED") { setLabelError("No shipping rate selected at checkout"); setLabelLoading(false); return; }
      }
      const result = await createShippingLabel(orderId);
      if (result?.ok) {
        // Use what the server saved. Buying a label is not shipping: the order
        // status stays as it is until the carrier scans the package.
        const updatedShipping = result.orderShipping || {
          ...order.shipping,
          status: "label_created",
          shipstation: {
            ...(order.shipping?.shipstation || {}),
            labelId: result.label?.labelId || "",
            trackingNumber: result.label?.trackingNumber || "",
            labelUrl: result.label?.labelUrl || "",
          },
        };
        onUpdate({ ...order, shipping: updatedShipping });
      }
    } catch (err) {
      const msg = err?.data?.message || err?.message || "Failed to create label";
      const code = err?.data?.code || "";
      setLabelError(code === "CARRIER_INSUFFICIENT_BALANCE" ? "Carrier has insufficient balance. Top up in ShipStation." : msg);
    } finally {
      setLabelLoading(false);
    }
  };

  // Add tracking by hand (shipped outside Moondala/ShipStation)
  const handleAddTracking = async () => {
    setTrackError("");
    const tn = trackNumber.replace(/\s+/g, "").trim();
    if (!tn) { setTrackError("Enter the tracking number"); return; }
    setTrackSaving(true);
    try {
      const res = await apiPost(`/shop/orders/${orderId}/tracking`, { carrier: trackCarrier, trackingNumber: tn });
      const o = res?.order || {};
      onUpdate({ ...order, status: o.status || order.status, shipping: o.shipping || order.shipping });
      setTrackNumber("");
    } catch (err) {
      setTrackError(err?.data?.message || err?.message || "Failed to add tracking");
    } finally {
      setTrackSaving(false);
    }
  };

  // Status change
  const handleStatus = async (newStatus) => {
    setStatusUpdating(true);
    try { await onStatus(orderId, newStatus); } finally { setStatusUpdating(false); }
  };

  // Return status update
  const handleReturnStatus = async (newStatus, shopNote) => {
    if (!returnId) return;
    setReturnUpdating(true);
    try {
      const body = { status: newStatus };
      if (shopNote !== undefined) body.shopNote = shopNote;
      const res = await apiPut(`/shop/returns/${returnId}/status`, body);
      const updated = res?.return || res;
      onReturnUpdate(orderId, updated);
      // If refunded, also update order status locally
      if (newStatus === "refunded") {
        onUpdate({ ...order, status: "refunded", returnStatus: "refunded" });
      } else {
        onUpdate({ ...order, returnStatus: newStatus });
      }
    } catch (err) {
      alert(err?.message || "Failed to update return");
    } finally {
      setReturnUpdating(false);
    }
  };

  // Issue refund (without return)
  const handleRefund = async () => {
    setRefundLoading(true);
    setRefundError("");
    try {
      const amt = Number(refundAmount || total);
      if (!amt || amt <= 0) { setRefundError("Enter a valid amount"); setRefundLoading(false); return; }
      if (amt > total) { setRefundError("Amount exceeds order total"); setRefundLoading(false); return; }

      // If there's an active return, update it to refunded with the amount
      if (hasReturn && returnId) {
        await apiPut(`/shop/returns/${returnId}/status`, {
          status: "refunded",
          refundAmount: amt,
          shopNote: refundNote || undefined,
        });
        onReturnUpdate(orderId, { ...returnReq, status: "refunded", refundAmount: amt, shopNote: refundNote });
        onUpdate({ ...order, status: "refunded", returnStatus: "refunded" });
      } else {
        // Refund without return — the refund endpoint returns the buyer's money.
        // If it fails the order stays as it is and the error is shown (marking
        // it "refunded" without a real refund would leave the buyer unpaid).
        await apiPost(`/shop/orders/${orderId}/refund`, {
          amount: amt,
          reason: refundNote || "Shop-initiated refund",
        });
        onUpdate({ ...order, status: "refunded" });
      }
      setRefundModalOpen(false);
      setRefundAmount("");
      setRefundNote("");
    } catch (err) {
      setRefundError(err?.message || "Failed to issue refund");
    } finally {
      setRefundLoading(false);
    }
  };

  return (
    <div className={`so-card ${expanded ? "so-card-expanded" : ""}`} onClick={toggleExpand}>
      {/* Card header */}
      <div className="so-card-top">
        <div className="so-card-ids">
          <input
            type="checkbox"
            className="so-card-checkbox"
            checked={isSelected}
            data-no-expand="true"
            onChange={(e) => { e.stopPropagation(); onToggleSelect(order._id); }}
          />
          <span className="so-order-id">#{shortId}</span>
          <span className="so-card-date">{date}</span>
        </div>
        <div className="so-card-badges">
          <PaymentBadge paid={isPaid} status={status} />
          <FulfillmentBadge status={fulfillmentStatus} />
          {hasReturn && <ReturnBadge status={returnStatus} />}
          <span className="so-expand-arrow">{expanded ? "▾" : "▸"}</span>
        </div>
      </div>

      {/* Product + customer row */}
      <div className="so-card-main">
        <div className="so-card-info">
          {lineItems.map((it, idx) => {
            const itImg = toAbsUrl(it.image) || (idx === 0 ? img : "");
            const pid = it.productId || (idx === 0 ? productId : "");
            return (
              <div className="so-card-line" key={idx} style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 8 }}>
                <div
                  className="so-card-img"
                  data-no-expand="true"
                  onClick={(e) => { e.stopPropagation(); if (pid) nav(`/shop/product/${pid}`); }}
                  title="Edit product"
                >
                  {itImg ? <img src={itImg} alt="" /> : <div className="so-card-no-img">📦</div>}
                </div>
                <div style={{ minWidth: 0 }}>
                  <div className="so-card-product-title">{it.title || "Unknown Product"}</div>
                  <div className="so-card-meta">Qty: {it.qty || 1}{idx === 0 ? ` · You earn ${currency} ${shopEarn.toFixed(2)}` : ""}</div>
                </div>
              </div>
            );
          })}
          <div
            className="so-card-customer so-card-customer-link"
            data-no-expand="true"
            onClick={(e) => {
              e.stopPropagation();
              const uid = order.user?._id || order.user?.id || order.userId || order.buyerId || "";
              if (uid) nav(`/shop/customers?userId=${uid}`);
            }}
          >👤 {customerName}</div>
        </div>
      </div>

      {/* Expanded details */}
      {expanded && (
        <div className="so-card-details" onClick={(e) => e.stopPropagation()}>

          {/* Timeline */}
          <OrderTimeline order={order} fulfillmentStatus={fulfillmentStatus} isPaid={isPaid} hasReturn={hasReturn} returnStatus={returnStatus} />

          {/* Order info */}
          <div className="so-detail-section">
            <div className="so-section-title">Order Details</div>
            <div className="so-detail-grid">
              <div className="so-detail-item"><span className="so-detail-label">Order ID</span><span className="so-detail-value so-mono">#{shortId}</span></div>
              <div className="so-detail-item"><span className="so-detail-label">Date</span><span className="so-detail-value">{fullDate}</span></div>
              <div className="so-detail-item"><span className="so-detail-label">Qty</span><span className="so-detail-value">{quantity}</span></div>
              <div className="so-detail-item"><span className="so-detail-label">Items</span><span className="so-detail-value">{currency} {itemsSubtotal.toFixed(2)}</span></div>
              <div className="so-detail-item"><span className="so-detail-label">Moondala fee (6%)</span><span className="so-detail-value">− {currency} {shopFee.toFixed(2)}</span></div>
              <div className="so-detail-item"><span className="so-detail-label">You earn</span><span className="so-detail-value so-bold">{currency} {shopEarn.toFixed(2)}</span></div>
              {order.payout?.status === "held" && (
                <div className="so-detail-item">
                  <span className="so-detail-label">Payout</span>
                  <span className="so-detail-value">
                    {order.payout?.releaseAt
                      ? `Paid to you on ${new Date(order.payout.releaseAt).toLocaleDateString()} (after the return window)`
                      : "Paid to you after delivery + the return window"}
                  </span>
                </div>
              )}
              <div className="so-detail-item"><span className="so-detail-label">Payment</span><span className="so-detail-value">{paymentMethod}</span></div>
              <div className="so-detail-item"><span className="so-detail-label">Status</span><span className="so-detail-value"><StatusBadge status={status} /></span></div>
            </div>
          </div>

          {/* Options */}
          {options.length > 0 && (
            <div className="so-detail-section">
              <div className="so-section-title">Options</div>
              <div className="so-options-row">
                {options.map((opt, i) => (
                  <span key={i} className="so-option-tag">{opt.name}: {opt.value}</span>
                ))}
              </div>
            </div>
          )}

          {/* Shipping address */}
          {addr && (
            <div className="so-detail-section">
              <div className="so-section-title">📍 Shipping Address</div>
              <div className="so-address-block">
                {addr.name && <div className="so-address-name">{addr.name}</div>}
                <div>{addr.street1 || addr.street || ""}{addr.street2 ? `, ${addr.street2}` : ""}</div>
                <div>{[addr.city, addr.state, addr.postalCode || addr.zip].filter(Boolean).join(", ")} {addr.country || ""}</div>
                {addr.phone && <div>📞 {addr.phone}</div>}
              </div>
            </div>
          )}

          {/* Shipping rate */}
          {selectedRate && (
            <div className="so-detail-section">
              <div className="so-section-title">🚚 Shipping Method</div>
              <div className="so-shipping-rate">
                <span>{selectedRate.carrierCode} / {selectedRate.serviceCode}</span>
                {selectedRate.deliveryDays && <span className="so-text-muted">({selectedRate.deliveryDays} days est.)</span>}
              </div>
            </div>
          )}

          {/* Fulfillment */}
          <div className="so-detail-section">
            <div className="so-section-title">📦 Fulfillment</div>
            {hasLabel ? (
              <div className="so-fulfillment-info">
                <div className="so-label-success">✅ Shipping label created</div>
                {trackingNum && (
                  <div className="so-tracking-row">
                    <span className="so-detail-label">Tracking:</span>
                    {trackingUrl ? (
                      <a href={trackingUrl} target="_blank" rel="noopener noreferrer" className="so-tracking-link" data-no-expand="true">{trackingNum}</a>
                    ) : (
                      <code className="so-tracking-code">{trackingNum}</code>
                    )}
                  </div>
                )}
                {labelUrl && (
                  <a href={labelUrl} target="_blank" rel="noopener noreferrer" className="so-btn so-btn-outline" data-no-expand="true" onClick={(e) => e.stopPropagation()}>
                    🖨️ Print Label
                  </a>
                )}
              </div>
            ) : isPaid && !["cancelled", "refunded", "completed"].includes(status) ? (
              <div className="so-fulfillment-info">
                {trackingNum ? (
                  <div className="so-tracking-row">
                    <span className="so-detail-label">Tracking:</span>
                    {trackingUrl ? (
                      <a href={trackingUrl} target="_blank" rel="noopener noreferrer" className="so-tracking-link" data-no-expand="true">{trackingNum}</a>
                    ) : (
                      <code className="so-tracking-code">{trackingNum}</code>
                    )}
                  </div>
                ) : (
                  <>
                    <div className="so-label-pending">⏳ No shipping label yet</div>
                    {labelError && <div className="so-label-error">⚠️ {labelError}</div>}
                    <button className="so-btn so-btn-primary so-btn-label" onClick={handleCreateLabel} disabled={labelLoading} data-no-expand="true">
                      {labelLoading ? "⏳ Creating label..." : "📦 Create Shipping Label"}
                    </button>
                    {/* Shipped another way: add the carrier's tracking so delivery (and your payout) is confirmed */}
                    <div className="so-manual-tracking" data-no-expand="true" style={{ marginTop: 12 }}>
                      <div className="so-detail-label" style={{ marginBottom: 6 }}>Shipped another way? Add tracking</div>
                      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
                        <select className="so-modal-input" style={{ width: "auto" }} value={trackCarrier} onChange={(e) => setTrackCarrier(e.target.value)} disabled={trackSaving}>
                          <option value="usps">USPS</option>
                          <option value="ups">UPS</option>
                          <option value="fedex">FedEx</option>
                          <option value="dhl">DHL Express</option>
                          <option value="other">Other</option>
                        </select>
                        <input
                          className="so-modal-input"
                          style={{ flex: "1 1 180px", minWidth: 0 }}
                          placeholder="Tracking number"
                          value={trackNumber}
                          onChange={(e) => setTrackNumber(e.target.value)}
                          disabled={trackSaving}
                        />
                        <button className="so-btn so-btn-default" onClick={handleAddTracking} disabled={trackSaving}>
                          {trackSaving ? "Saving..." : "Add tracking"}
                        </button>
                      </div>
                      {trackError && <div className="so-label-error" style={{ marginTop: 6 }}>⚠️ {trackError}</div>}
                      {trackCarrier === "other" && (
                        <div className="so-text-muted" style={{ marginTop: 4, fontSize: 12 }}>
                          Delivery can only be confirmed automatically for USPS, UPS, FedEx and DHL. For other carriers contact Moondala support once it is delivered.
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>
            ) : (
              <div className="so-fulfillment-info">
                <div className="so-text-muted">{!isPaid ? "Awaiting payment" : `Order ${status}`}</div>
              </div>
            )}
          </div>

          {/* ── RETURN SECTION ── */}
          {hasReturn && (
            <div className="so-detail-section so-return-section">
              <div className="so-section-title">↩️ Return Request</div>
              <div className="so-return-header">
                <ReturnBadge status={returnStatus} />
                <span className="so-text-muted">Requested {returnReq.createdAt ? new Date(returnReq.createdAt).toLocaleDateString() : ""}</span>
              </div>

              <div className="so-detail-grid" style={{ marginTop: 10 }}>
                <div className="so-detail-item"><span className="so-detail-label">Reason</span><span className="so-detail-value">{returnReason || "Not specified"}</span></div>
                <div className="so-detail-item"><span className="so-detail-label">Refund Amount</span><span className="so-detail-value so-bold">{currency} {returnRefundAmount.toFixed(2)}</span></div>
              </div>

              {returnNote && (
                <div className="so-return-note">
                  <span className="so-detail-label">Customer Note</span>
                  <div className="so-note-text">{returnNote}</div>
                </div>
              )}

              {returnShopNote && (
                <div className="so-return-note">
                  <span className="so-detail-label">Your Note</span>
                  <div className="so-note-text">{returnShopNote}</div>
                </div>
              )}

              {/* Return action buttons */}
              <div className="so-return-actions" data-no-expand="true">
                {returnUpdating ? (
                  <span className="so-updating">Updating...</span>
                ) : (
                  <>
                    {returnStatus === "requested" && (
                      <>
                        <button className="so-btn so-btn-success" onClick={() => handleReturnStatus("approved")}>✅ Approve Return</button>
                        <button className="so-btn so-btn-danger" onClick={() => {
                          const note = prompt("Reason for rejection (optional):");
                          if (note === null) return; // user cancelled prompt
                          handleReturnStatus("rejected", note || "");
                        }}>❌ Reject</button>
                      </>
                    )}
                    {returnStatus === "approved" && (
                      <>
                        <button className="so-btn so-btn-default" onClick={async () => {
                          try {
                            const res = await apiPost(`/shipping/orders/${orderId}/return-label`);
                            const lbl = res?.returnLabel || res?.returnShipping || null;
                            if (res?.alreadyExists) {
                              if (lbl?.labelUrl) window.open(lbl.labelUrl, "_blank");
                              else alert("A return label already exists for this return.");
                            } else if (lbl?.labelUrl) {
                              window.open(lbl.labelUrl, "_blank");
                              alert("Return label created! Tracking: " + (lbl.trackingNumber || "N/A"));
                              if (returnReq) onReturnUpdate(orderId, { ...returnReq, returnShipping: { ...(returnReq.returnShipping || {}), ...lbl } });
                            }
                          } catch (err) {
                            alert(err?.message || "Failed to create return label");
                          }
                        }}>🏷️ Create Return Label</button>
                        <button className="so-btn so-btn-default" onClick={() => handleReturnStatus("shipped_back")}>📦 Mark Shipped Back</button>
                      </>
                    )}
                    {returnStatus === "shipped_back" && (
                      <button className="so-btn so-btn-default" onClick={() => handleReturnStatus("received")}>📬 Mark Received</button>
                    )}
                    {(returnStatus === "received" || returnStatus === "approved") && returnStatus !== "refunded" && (
                      <button className="so-btn so-btn-refund" onClick={() => { setRefundAmount(String(returnRefundAmount)); setRefundModalOpen(true); }}>
                        💰 Issue Refund
                      </button>
                    )}
                  </>
                )}
              </div>
            </div>
          )}

          {/* Customer */}
          <div className="so-detail-section">
            <div className="so-section-title">👤 Customer</div>
            <div className="so-detail-grid">
              <div className="so-detail-item"><span className="so-detail-label">Name</span><span className="so-detail-value so-customer-link" onClick={() => {
                const uid = order.user?._id || order.user?.id || order.userId || order.buyerId || "";
                if (uid) nav(`/shop/customers?userId=${uid}`);
              }}>{customerName}</span></div>
              <div className="so-detail-item"><span className="so-detail-label">Email</span><span className="so-detail-value">{customerEmail}</span></div>
              {(order.user?.phone || addr?.phone) && (
                <div className="so-detail-item"><span className="so-detail-label">Phone</span><span className="so-detail-value">{order.user?.phone || addr?.phone}</span></div>
              )}
            </div>
          </div>

          {/* Action buttons */}
          <div className="so-actions" data-no-expand="true">
            {statusUpdating ? (
              <span className="so-updating">Updating...</span>
            ) : (
              <>
                {PAID_UNSHIPPED.includes(status) && <button className="so-btn so-btn-primary" onClick={() => handleStatus("shipped")}>🚚 Mark Shipped</button>}
                {status === "shipped" && <span style={{ fontSize: 12, opacity: 0.75 }} title="Delivery is confirmed by the carrier's tracking. Your payout is released after the return window.">🚚 Delivery confirmed by tracking</span>}
                {/* Only an unpaid order can be cancelled; a paid one is cancelled via Refund */}
                {!isPaid && !["cancelled", "completed", "refunded"].includes(status) && (
                  <button className="so-btn so-btn-danger" onClick={() => handleStatus("cancelled")}>Cancel</button>
                )}
                {/* Refund without return (for any paid order) */}
                {isPaid && !["refunded"].includes(status) && !hasReturn && (
                  <button className="so-btn so-btn-refund" onClick={() => { setRefundAmount(String(total)); setRefundModalOpen(true); }}>
                    💰 Refund
                  </button>
                )}
              </>
            )}
          </div>

          {/* Refund Modal */}
          {refundModalOpen && (
            <div className="so-modal-overlay" onClick={() => !refundLoading && setRefundModalOpen(false)}>
              <div className="so-modal" onClick={(e) => e.stopPropagation()}>
                <div className="so-modal-title">💰 Issue Refund</div>
                <div className="so-modal-subtitle">
                  Order #{shortId} · {customerName}
                </div>

                {refundError && <div className="so-label-error" style={{ marginTop: 10 }}>⚠️ {refundError}</div>}

                <div className="so-modal-field">
                  <label className="so-detail-label">Refund Amount ({currency})</label>
                  <input
                    type="number"
                    className="so-modal-input"
                    value={refundAmount}
                    onChange={(e) => setRefundAmount(e.target.value)}
                    max={total}
                    min={0}
                    step="0.01"
                    disabled={refundLoading || (hasReturn && !!returnId)}
                    placeholder={total.toFixed(2)}
                  />
                  <div className="so-text-muted" style={{ marginTop: 4 }}>
                    {hasReturn && returnId
                      ? "For a return, Moondala works out the refund from the returned items and your return policy (return shipping and restocking fee are deducted when the buyer changed their mind)."
                      : <>Order total: {currency} {total.toFixed(2)} · {Number(refundAmount) < total ? "Partial refund" : "Full refund"}</>}
                  </div>
                </div>

                <div className="so-modal-field">
                  <label className="so-detail-label">Note (optional)</label>
                  <textarea
                    className="so-modal-textarea"
                    value={refundNote}
                    onChange={(e) => setRefundNote(e.target.value)}
                    placeholder="Internal note about this refund..."
                    disabled={refundLoading}
                  />
                </div>

                <div className="so-modal-actions">
                  <button className="so-btn so-btn-default" onClick={() => setRefundModalOpen(false)} disabled={refundLoading}>Cancel</button>
                  <button className="so-btn so-btn-refund" onClick={handleRefund} disabled={refundLoading}>
                    {refundLoading ? "Processing..." : `Refund ${currency} ${Number(refundAmount || 0).toFixed(2)}`}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* =========================================================
   ORDER TIMELINE
   ========================================================= */
function OrderTimeline({ order, fulfillmentStatus, isPaid, hasReturn, returnStatus }) {
  const status = (order.status || "pending").toLowerCase();
  const isCancelled = status === "cancelled";
  const isRefunded = status === "refunded" || returnStatus === "refunded";

  if (isCancelled) {
    return (
      <div className="so-timeline-cancelled">
        <span className="so-timeline-cancel-icon">❌</span>
        <span>Order cancelled</span>
        {order.cancelledByUserAt && <span className="so-text-muted"> · {new Date(order.cancelledByUserAt).toLocaleDateString()}</span>}
      </div>
    );
  }

  if (isRefunded) {
    return (
      <div className="so-timeline-refunded">
        <span className="so-timeline-cancel-icon">💰</span>
        <span>Order refunded</span>
      </div>
    );
  }

  const steps = [
    { key: "ordered", label: "Ordered", icon: "🛒" },
    { key: "paid", label: "Paid", icon: "💳" },
    { key: "label", label: "Label", icon: "🏷️" },
    { key: "shipped", label: "Shipped", icon: "🚚" },
    { key: "delivered", label: "Delivered", icon: "✅" },
  ];

  if (hasReturn) {
    steps.push({ key: "return", label: "Return", icon: "↩️" });
  }

  const isComplete = (key) => {
    switch (key) {
      case "ordered": return true;
      case "paid": return isPaid;
      case "label": return ["label_created", "shipped", "out_for_delivery", "delivered"].includes(fulfillmentStatus);
      case "shipped": return ["shipped", "out_for_delivery", "delivered"].includes(fulfillmentStatus);
      case "delivered": return fulfillmentStatus === "delivered";
      case "return": return ["approved", "shipped_back", "received", "refunded"].includes(returnStatus);
      default: return false;
    }
  };

  return (
    <div className="so-timeline">
      {steps.map((step, i) => {
        const done = isComplete(step.key);
        const isLast = i === steps.length - 1;
        return (
          <React.Fragment key={step.key}>
            <div className={`so-timeline-step ${done ? "so-timeline-done" : "so-timeline-pending"}`}>
              <div className="so-timeline-icon">{done ? "✓" : step.icon}</div>
              <div className="so-timeline-label">{step.label}</div>
            </div>
            {!isLast && <div className={`so-timeline-line ${done ? "so-timeline-line-done" : ""}`} />}
          </React.Fragment>
        );
      })}
    </div>
  );
}

/* =========================================================
   BADGES
   ========================================================= */
function PaymentBadge({ paid, status }) {
  if (status === "refunded") return <span className="so-badge so-badge-purple">REFUNDED</span>;
  if (paid) return <span className="so-badge so-badge-green">PAID</span>;
  return <span className="so-badge so-badge-gray">UNPAID</span>;
}

function FulfillmentBadge({ status }) {
  const map = {
    unfulfilled: { cls: "so-badge-orange", label: "UNFULFILLED" },
    label_created: { cls: "so-badge-blue", label: "LABEL CREATED" },
    shipped: { cls: "so-badge-yellow", label: "SHIPPED" },
    out_for_delivery: { cls: "so-badge-yellow", label: "OUT FOR DELIVERY" },
    delivered: { cls: "so-badge-green", label: "DELIVERED" },
    cancelled: { cls: "so-badge-red", label: "CANCELLED" },
    refunded: { cls: "so-badge-purple", label: "REFUNDED" },
  };
  const info = map[status] || map.unfulfilled;
  return <span className={`so-badge ${info.cls}`}>{info.label}</span>;
}

function ReturnBadge({ status }) {
  const map = {
    requested: { cls: "so-badge-orange", label: "RETURN REQUESTED" },
    approved: { cls: "so-badge-blue", label: "RETURN APPROVED" },
    rejected: { cls: "so-badge-red", label: "RETURN REJECTED" },
    shipped_back: { cls: "so-badge-yellow", label: "SHIPPED BACK" },
    received: { cls: "so-badge-blue", label: "ITEM RECEIVED" },
    refunded: { cls: "so-badge-purple", label: "REFUNDED" },
  };
  const info = map[status] || map.requested;
  return <span className={`so-badge ${info.cls}`}>{info.label}</span>;
}

function StatusBadge({ status }) {
  const s = (status || "").toLowerCase();
  const map = {
    pending: "so-badge-gray", pending_payment: "so-badge-gray",
    paid: "so-badge-blue", paid_msc: "so-badge-blue", paid_mixed: "so-badge-blue",
    shipped: "so-badge-yellow", completed: "so-badge-green", delivered: "so-badge-green",
    cancelled: "so-badge-red", canceled: "so-badge-red",
    refunded: "so-badge-purple", returned: "so-badge-orange",
    payment_failed: "so-badge-red", transfer_pending: "so-badge-yellow", transferred: "so-badge-green",
  };
  const labels = {
    completed: "DELIVERED", pending_payment: "AWAITING PAYMENT",
    paid_msc: "PAID (MSC)", paid_mixed: "PAID (MIXED)",
    payment_failed: "PAYMENT FAILED", transfer_pending: "TRANSFER PENDING",
  };
  return <span className={`so-badge ${map[s] || "so-badge-gray"}`}>{labels[s] || s.toUpperCase()}</span>;
}
